import React, { useState } from "react";
import { CRMFilterSearchSelect } from "../../Complex/CRMFilterSearchSelect/CRMFilterSearchSelect";
import { IFuzzySortOption } from "../../../hooks/useFuzzysort";
import { TCasesDocumentType, toDisplayString as CaseDocumentTypeToCopyText, selectableValues } from "../../../../../domain/models/CaseDocumentType1";
import { CRMFilterAndSelectTinyArrowOptionWrap } from "../../Simple/CRMFilterAndSelectTinyArrowOptionWrap/CRMFilterAndSelectTinyArrowOptionWrap";
import { CRMFuzzySortSearchHighlight } from "../../Simple/CRMFuzzySortSearchHighlight/CRMFuzzySortSearchHighlight";
import { Relative } from "../../BuildingBlocks/Relative";
import { Background } from "../../BuildingBlocks/Background";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { WeightMedium } from "../../WeightMedium/WeightMedium";

export type TCaseDocumentTypeTileInfo = {
    cases_document_id: string;
    type: TCasesDocumentType;
    additional_name: string;
    awaiting: boolean;
};

export type TCaseDocumentTypeSelection =
    | { tag: "existing_tile"; cases_document_id: string; type: TCasesDocumentType; additional_name: string }
    | { tag: "new_tile"; type: TCasesDocumentType };

type TCaseDocumentTypeSelectMeta = {
    selection: TCaseDocumentTypeSelection;
    groupLabel: string;
};

type TCRMCaseDocumentTypeSelectProps = {
    existingTiles: ReadonlyArray<TCaseDocumentTypeTileInfo>;
    searchStickyTopPosition?: string;
    searchPlaceholder?: string;
    onSelect: (selection: TCaseDocumentTypeSelection) => void;
};

const makeOption = (
    meta: TCaseDocumentTypeSelectMeta,
    label: string,
    valueKey: string,
    onClick: () => void,
): IFuzzySortOption<string, TCaseDocumentTypeSelectMeta> => ({
    label,
    value: valueKey,
    meta,
    richLabelFormatting: (_result, searchText) => (
        <CRMFilterAndSelectTinyArrowOptionWrap onClick={onClick}>
            <SpacingColumn spacing={CRMSpacing.TINY}>
                {meta.groupLabel !== "" && (
                    <CRMParagraph fontSize={CRMFontSizes.X_SMALL}>
                        <WeightMedium>
                            <span className="crm-case-document-type-select__secondary-text">
                                {meta.groupLabel}
                            </span>
                        </WeightMedium>
                    </CRMParagraph>
                )}
                <CRMParagraph fontSize={CRMFontSizes.SMALL}>
                    <CRMFuzzySortSearchHighlight text={label} searchText={searchText} />
                </CRMParagraph>
            </SpacingColumn>
        </CRMFilterAndSelectTinyArrowOptionWrap>
    ),
});

export const CRMCaseDocumentTypeSelect = (props: React.PropsWithChildren<TCRMCaseDocumentTypeSelectProps>): JSX.Element => {
    const [searchText, setSearchText] = useState<string>("");
    const hasSearch = searchText.trim().length > 0;

    const awaitingTiles = props.existingTiles.filter((t) => t.awaiting);
    const otherExistingTiles = props.existingTiles.filter((t) => !t.awaiting);

    const buildTileLabel = (t: TCaseDocumentTypeTileInfo) =>
        t.type === "other" && t.additional_name
            ? `${CaseDocumentTypeToCopyText(t.type)} — ${t.additional_name}`
            : CaseDocumentTypeToCopyText(t.type);

    const awaitingOptions = awaitingTiles.map((t) =>
        makeOption(
            {
                selection: { tag: "existing_tile", cases_document_id: t.cases_document_id, type: t.type, additional_name: t.additional_name },
                groupLabel: "Awaiting",
            },
            buildTileLabel(t),
            `awaiting-${t.cases_document_id}`,
            () => props.onSelect({ tag: "existing_tile", cases_document_id: t.cases_document_id, type: t.type, additional_name: t.additional_name }),
        )
    );

    const otherExistingOptions = otherExistingTiles.map((t) =>
        makeOption(
            {
                selection: { tag: "existing_tile", cases_document_id: t.cases_document_id, type: t.type, additional_name: t.additional_name },
                groupLabel: "Existing tile",
            },
            buildTileLabel(t),
            `existing-${t.cases_document_id}`,
            () => props.onSelect({ tag: "existing_tile", cases_document_id: t.cases_document_id, type: t.type, additional_name: t.additional_name }),
        )
    );

    const newTileOptions = selectableValues
        .map((type) =>
            makeOption(
                { selection: { tag: "new_tile", type }, groupLabel: "New tile" },
                CaseDocumentTypeToCopyText(type),
                `new-${type}`,
                () => props.onSelect({ tag: "new_tile", type }),
            )
        );

    const visibleOptions = hasSearch
        ? [...awaitingOptions, ...otherExistingOptions, ...newTileOptions]
        : awaitingOptions;

    const hasAwaiting = awaitingOptions.length > 0;
    const placeholder = props.searchPlaceholder
        || (hasAwaiting ? "Search to see all document types" : "Search for a document type");

    return (
        <Relative>
            {!hasSearch && !hasAwaiting && (
                <Background padding={CRMSpacing.MEDIUM}>
                    <CRMParagraph fontSize={CRMFontSizes.SMALL}>
                        <WeightMedium>
                            <span className="crm-case-document-type-select__secondary-text">
                                No tiles are awaiting files. Start typing to pick or add a document type.
                            </span>
                        </WeightMedium>
                    </CRMParagraph>
                </Background>
            )}

            <CRMFilterSearchSelect<TCaseDocumentTypeSelectMeta>
                searchStickyTopPosition={props.searchStickyTopPosition as `${number}px` | undefined}
                searchPlaceholder={placeholder}
                noMatchesText="No matching document types."
                keepOriginalSearchResultsOrder={true}
                options={visibleOptions}
                onChangeSearchText={setSearchText}
            />
        </Relative>
    );
};
